
import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface SiteSettings {
  id?: number;
  logo?: string;
  logo_img?: string;
}

interface OpponentTeam {
  id: string;
  name: string;
  logo?: string;
  logo_img?: string;
  city?: string;
}

interface Sponsor {
  id: string;
  name: string;
  logo?: string;
  logo_img?: string;
  website?: string;
  created_at?: string;
}

const SiteSettingsManager: React.FC = () => {
  const [settings, setSettings] = useState<SiteSettings>({});
  const [logoUrl, setLogoUrl] = useState('');
  const [logoImg, setLogoImg] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Estados para equipos rivales
  const [opponentTeams, setOpponentTeams] = useState<OpponentTeam[]>([]);
  const [showOpponentForm, setShowOpponentForm] = useState(false);
  const [editingOpponent, setEditingOpponent] = useState<OpponentTeam | null>(null);
  const [opponentForm, setOpponentForm] = useState({
    name: '',
    logo: '',
    logo_img: '',
    city: ''
  });
  
  // Estados para patrocinadores
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [showSponsorForm, setShowSponsorForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
  const [sponsorForm, setSponsorForm] = useState({
    name: '',
    logo: '',
    logo_img: '',
    website: ''
  });
  const [showSponsorImageEditor, setShowSponsorImageEditor] = useState(false);
  const [sponsorImageEditorData, setSponsorImageEditorData] = useState({
    originalImage: '',
    croppedImage: '',
    type: 'sponsor_logo' as const
  });
  const [uploadingSponsorLogo, setUploadingSponsorLogo] = useState(false);
  
  const [activeTab, setActiveTab] = useState<'logo' | 'opponents' | 'sponsors'>('logo');

  // Estados para el editor de imágenes de equipos rivales
  const [showOpponentImageEditor, setShowOpponentImageEditor] = useState(false);
  const [opponentImageEditorData, setOpponentImageEditorData] = useState({
    originalImage: '',
    croppedImage: '',
    type: 'opponent_logo' as const
  });
  const [uploadingOpponentLogo, setUploadingOpponentLogo] = useState(false);

  useEffect(() => {
    loadSettings();
    loadOpponentTeams();
    loadSponsors();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading settings:', error);
        return;
      }

      if (data) {
        setSettings(data);
        setLogoUrl(data.logo || '');
        setLogoImg(data.logo_img || '');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadOpponentTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('opponent_teams')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error loading opponent teams:', error);
        return;
      }

      setOpponentTeams(data || []);
    } catch (error) {
      console.error('Error loading opponent teams:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        setMessage('Por favor selecciona un archivo de imagen válido');
        return;
      }
      
      // Validar tamaño (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        setMessage('El archivo es demasiado grande. Máximo 5MB');
        return;
      }
      
      setSelectedFile(file);
      setMessage('');
    }
  };

  const uploadImage = async () => {
    if (!selectedFile) return null;

    setUploading(true);
    try {
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, selectedFile);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage('');

    try {
      let uploadedImageUrl = logoImg;

      // Si hay un archivo seleccionado, subirlo primero
      if (selectedFile) {
        uploadedImageUrl = await uploadImage();
        if (!uploadedImageUrl) {
          throw new Error('Error al subir la imagen');
        }
      }

      const settingsData = {
        logo: logoUrl || null,
        logo_img: uploadedImageUrl || null,
        updated_at: new Date().toISOString()
      };

      if (settings.id) {
        const { error } = await supabase
          .from('site_settings')
          .update(settingsData)
          .eq('id', settings.id);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('site_settings')
          .insert([settingsData])
          .select()
          .single();

        if (error) throw error;
        setSettings(data);
      }

      setLogoImg(uploadedImageUrl || '');
      setSelectedFile(null);
      setMessage('Configuración guardada exitosamente');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Error al guardar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setLogoUrl('');
    setLogoImg('');
    setSelectedFile(null);
  };

  const generateStableDiffusionUrl = () => {
    const prompt = 'professional basketball logo, modern design, clean minimalist style, sports team emblem, high quality vector style, isolated on white background, centered composition';
    const url = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28prompt%29%7D&width=200&height=200&seq=${Date.now()}&orientation=squarish`;
    setLogoUrl(url);
  };

  // Función para subir imagen de equipo rival
  const uploadOpponentImage = async (file: File) => {
    setUploadingOpponentLogo(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `opponent-logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading opponent image:', error);
      throw error;
    } finally {
      setUploadingOpponentLogo(false);
    }
  };

  // Función para abrir selector de medios para equipos rivales
  const openOpponentMediaSelector = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // Validar tipo de archivo
        if (!file.type.startsWith('image/')) {
          setMessage('Por favor selecciona un archivo de imagen válido');
          return;
        }
        
        // Validar tamaño (5MB máximo)
        if (file.size > 5 * 1024 * 1024) {
          setMessage('El archivo es demasiado grande. Máximo 5MB');
          return;
        }

        try {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            setOpponentImageEditorData({
              originalImage: result,
              croppedImage: result,
              type: 'opponent_logo'
            });
            setShowOpponentImageEditor(true);
          };
          reader.onerror = () => {
            setMessage('Error al leer el archivo. Por favor intenta de nuevo.');
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error al procesar imagen:', error);
          setMessage('Error al procesar la imagen');
        }
      }
    };
    input.click();
  };

  // Función para guardar imagen editada de equipo rival
  const handleOpponentImageSave = async () => {
    try {
      // Convertir la imagen editada a blob
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = async () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        
        canvas.toBlob(async (blob) => {
          if (blob) {
            const file = new File([blob], 'opponent-logo.jpg', { type: 'image/jpeg' });
            const uploadedUrl = await uploadOpponentImage(file);
            setOpponentForm({ ...opponentForm, logo_img: uploadedUrl });
            setShowOpponentImageEditor(false);
            setOpponentImageEditorData({ originalImage: '', croppedImage: '', type: 'opponent_logo' });
          }
        }, 'image/jpeg', 0.9);
      };
      
      img.src = opponentImageEditorData.croppedImage;
    } catch (error) {
      console.error('Error al guardar imagen:', error);
      setMessage('Error al guardar la imagen');
    }
  };

  // Funciones para equipos rivales
  const handleOpponentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const opponentData = {
        name: opponentForm.name.trim(),
        logo: opponentForm.logo.trim() || null,
        logo_img: opponentForm.logo_img.trim() || null,
        city: opponentForm.city.trim() || null
      };

      if (editingOpponent) {
        const { error } = await supabase
          .from('opponent_teams')
          .update(opponentData)
          .eq('id', editingOpponent.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('opponent_teams')
          .insert([opponentData]);

        if (error) throw error;
      }

      setOpponentForm({ name: '', logo: '', logo_img: '', city: '' });
      setShowOpponentForm(false);
      setEditingOpponent(null);
      await loadOpponentTeams();
      setMessage('Equipo rival guardado exitosamente');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving opponent team:', error);
      setMessage('Error al guardar el equipo rival');
    } finally {
      setLoading(false);
    }
  };

  const editOpponent = (team: OpponentTeam) => {
    setOpponentForm({
      name: team.name,
      logo: team.logo || '',
      logo_img: team.logo_img || '',
      city: team.city || ''
    });
    setEditingOpponent(team);
    setShowOpponentForm(true);
  };

  const deleteOpponent = async (id: string) => {
    if (confirm('¿Estás seguro de eliminar este equipo rival?')) {
      try {
        const { error } = await supabase
          .from('opponent_teams')
          .delete()
          .eq('id', id);

        if (error) throw error;

        await loadOpponentTeams();
        setMessage('Equipo rival eliminado exitosamente');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('Error deleting opponent team:', error);
        setMessage('Error al eliminar el equipo rival');
      }
    }
  };

  // Funciones para patrocinadores
  const loadSponsors = async () => {
    try {
      const { data, error } = await supabase
        .from('sponsors')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading sponsors:', error);
        return;
      }

      setSponsors(data || []);
    } catch (error) {
      console.error('Error loading sponsors:', error);
    }
  };

  const uploadSponsorImage = async (file: File) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `sponsor-logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading sponsor image:', error);
      throw error;
    }
  };

  const openSponsorMediaSelector = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          setMessage('Por favor selecciona un archivo de imagen válido');
          return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
          setMessage('El archivo es demasiado grande. Máximo 5MB');
          return;
        }

        try {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            setSponsorImageEditorData({
              originalImage: result,
              croppedImage: result,
              type: 'sponsor_logo'
            });
            setShowSponsorImageEditor(true);
          };
          reader.onerror = () => {
            setMessage('Error al leer el archivo. Por favor intenta de nuevo.');
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error al procesar imagen:', error);
          setMessage('Error al procesar la imagen');
        }
      }
    };
    input.click();
  };

  const handleSponsorImageSave = async () => {
    try {
      setUploadingSponsorLogo(true);
      setMessage('Procesando imagen...');
      
      // Usar la imagen recortada si existe, sino la original
      const imageToUse = sponsorImageEditorData.croppedImage || sponsorImageEditorData.originalImage;
      
      if (!imageToUse) {
        setMessage('No hay imagen para procesar');
        setUploadingSponsorLogo(false);
        return;
      }

      // Convertir data URL a blob y luego a File
      const response = await fetch(imageToUse);
      const blob = await response.blob();
      const file = new File([blob], `sponsor-logo-${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Subir a Supabase
      const uploadedUrl = await uploadSponsorImage(file);
      
      // Actualizar el formulario con la URL subida
      setSponsorForm(prev => ({ ...prev, logo_img: uploadedUrl }));
      setShowSponsorImageEditor(false);
      setSponsorImageEditorData({ originalImage: '', croppedImage: '', type: 'sponsor_logo' });
      setMessage('Logo subido exitosamente');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error al guardar imagen:', error);
      setMessage(`Error al subir la imagen: ${error instanceof Error ? error.message : 'Error desconocido'}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setUploadingSponsorLogo(false);
    }
  };

  const handleSponsorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const sponsorData = {
        name: sponsorForm.name.trim(),
        logo: sponsorForm.logo.trim() || null,
        logo_img: sponsorForm.logo_img.trim() || null,
        website: sponsorForm.website.trim() || null
      };

      if (editingSponsor) {
        const { error } = await supabase
          .from('sponsors')
          .update(sponsorData)
          .eq('id', editingSponsor.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('sponsors')
          .insert([sponsorData]);

        if (error) throw error;
      }

      setSponsorForm({ name: '', logo: '', logo_img: '', website: '' });
      setShowSponsorForm(false);
      setEditingSponsor(null);
      await loadSponsors();
      setMessage('Patrocinador guardado exitosamente');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving sponsor:', error);
      setMessage('Error al guardar el patrocinador');
    } finally {
      setLoading(false);
    }
  };

  const editSponsor = (sponsor: Sponsor) => {
    setSponsorForm({
      name: sponsor.name,
      logo: sponsor.logo || '',
      logo_img: sponsor.logo_img || '',
      website: sponsor.website || ''
    });
    setEditingSponsor(sponsor);
    setShowSponsorForm(true);
  };

  const deleteSponsor = async (id: string) => {
    if (confirm('¿Estás seguro de eliminar este patrocinador?')) {
      try {
        const { error } = await supabase
          .from('sponsors')
          .delete()
          .eq('id', id);

        if (error) throw error;

        await loadSponsors();
        setMessage('Patrocinador eliminado exitosamente');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('Error deleting sponsor:', error);
        setMessage('Error al eliminar el patrocinador');
      }
    }
  };

  const generateOpponentLogo = () => {
    const prompt = 'professional basketball team logo, modern design, clean minimalist style, sports emblem, high quality vector style, isolated on white background, centered composition';
    const url = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28prompt%29%7D&width=200&height=200&seq=${Date.now()}&orientation=squarish`;
    setOpponentForm({ ...opponentForm, logo: url });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Configuración del Sitio</h2>
      
      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
        <button
          onClick={() => setActiveTab('logo')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            activeTab === 'logo'
              ? 'bg-blue-500 text-white'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
          }`}
        >
          Logo del Sitio
        </button>
        <button
          onClick={() => setActiveTab('opponents')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            activeTab === 'opponents'
              ? 'bg-blue-500 text-white'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
          }`}
        >
          Equipos Rivales
        </button>
        <button
          onClick={() => setActiveTab('sponsors')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            activeTab === 'sponsors'
              ? 'bg-blue-500 text-white'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
          }`}
        >
          Patrocinadores
        </button>
      </div>

      {/* Contenido según tab activo */}
      {activeTab === 'logo' && (
        <div className="space-y-6">
          {/* Logo desde Medios */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo desde Medios
            </label>
            
            <div className="space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">Subir Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Selecciona una imagen desde tu dispositivo (máx. 5MB)
                </p>
              </div>

              {/* Selected File Preview */}
              {selectedFile && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    <i className="ri-file-image-line mr-1"></i>
                    Archivo seleccionado: {selectedFile.name}
                  </p>
                  <p className="text-xs text-blue-600">
                    Tamaño: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}

              {/* Current Logo Preview */}
              {logoImg && (
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Logo Actual</label>
                  <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                    <img
                      src={logoImg}
                      alt="Logo actual"
                      className="h-16 w-auto object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Separador */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-4">O usar URL externa</h3>
          </div>

          {/* Logo Configuration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo por URL
            </label>
            
            <div className="space-y-4">
              {/* URL Input */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">URL del Logo</label>
                <input
                  type="url"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  placeholder="https://ejemplo.com/logo.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Introduce la URL completa de tu logo
                </p>
              </div>

              {/* Generate Sample Button */}
              <div>
                <button
                  type="button"
                  onClick={generateStableDiffusionUrl}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm"
                >
                  Generar Logo de Ejemplo
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Genera un logo de baloncesto de ejemplo
                </p>
              </div>

              {/* Preview */}
              {logoUrl && (
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Vista Previa URL</label>
                  <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                    <img
                      src={logoUrl}
                      alt="Logo preview"
                      className="h-16 w-auto object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={loading || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {loading ? 'Guardando...' : uploading ? 'Subiendo...' : 'Guardar Cambios'}
            </button>
            
            <button
              onClick={handleReset}
              type="button"
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors text-sm"
            >
              Restablecer
            </button>
          </div>

          {/* Examples */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Opciones disponibles:</h3>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• <strong>Subir desde medios:</strong> Selecciona una imagen desde tu dispositivo</li>
              <li>• <strong>URL externa:</strong> Usa una imagen que ya esté en internet</li>
              <li>• <strong>Generar ejemplo:</strong> Crea un logo automáticamente</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'opponents' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Equipos Rivales</h3>
              <p className="text-sm text-gray-500">Gestiona los equipos rivales para los partidos</p>
            </div>
            <button
              onClick={() => setShowOpponentForm(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
            >
              <i className="ri-add-line"></i>
              Añadir Equipo Rival
            </button>
          </div>

          {/* Lista de equipos rivales */}
          <div className="space-y-3">
            {opponentTeams.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-team-line text-4xl mb-2"></i>
                <p>No hay equipos rivales registrados</p>
                <p className="text-sm">Añade el primer equipo rival para comenzar</p>
              </div>
            ) : (
              opponentTeams.map((team) => (
                <div key={team.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {(team.logo_img || team.logo) && (
                      <img
                        src={team.logo_img || team.logo}
                        alt={team.name}
                        className="w-12 h-12 rounded-full object-cover border border-gray-200"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    )}
                    <div>
                      <h4 className="font-medium text-gray-900">{team.name}</h4>
                      {team.city && (
                        <p className="text-sm text-gray-500">
                          <i className="ri-map-pin-line mr-1"></i>
                          {team.city}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => editOpponent(team)}
                      className="text-blue-600 hover:text-blue-700 p-2 rounded-md hover:bg-blue-50 transition-colors"
                      title="Editar"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => deleteOpponent(team.id)}
                      className="text-red-600 hover:text-red-700 p-2 rounded-md hover:bg-red-50 transition-colors"
                      title="Eliminar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Formulario de Equipo Rival */}
      {showOpponentForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <form onSubmit={handleOpponentSubmit} className="bg-white rounded-lg p-6 w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900">
              {editingOpponent ? 'Editar Equipo Rival' : 'Añadir Equipo Rival'}
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Equipo</label>
              <input
                type="text"
                value={opponentForm.name}
                onChange={(e) => setOpponentForm({...opponentForm, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Ej: CB Barcelona"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
              <input
                type="text"
                value={opponentForm.city}
                onChange={(e) => setOpponentForm({...opponentForm, city: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Ej: Barcelona"
              />
            </div>
            
            {/* Logo desde Medios */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo desde Medios</label>
              <button
                type="button"
                onClick={openOpponentMediaSelector}
                disabled={uploadingOpponentLogo}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border-2 border-dashed border-gray-300 transition-colors disabled:opacity-50"
              >
                <i className="ri-upload-line mr-2"></i>
                {uploadingOpponentLogo ? 'Subiendo...' : opponentForm.logo_img ? 'Cambiar Logo' : 'Subir Logo desde Dispositivo'}
              </button>
              {opponentForm.logo_img && (
                <div className="mt-3">
                  <p className="text-sm text-gray-500 mb-2">Logo subido:</p>
                  <img
                    src={opponentForm.logo_img}
                    alt="Logo subido"
                    className="w-16 h-16 rounded-full object-cover border border-gray-200 mx-auto"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setOpponentForm({ ...opponentForm, logo_img: '' })}
                    className="mt-2 text-sm text-red-600 hover:text-red-700 block mx-auto"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Eliminar logo subido
                  </button>
                </div>
              )}
            </div>

            {/* Separador */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">O usar URL externa</h4>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo (URL)</label>
              <div className="space-y-2">
                <input
                  type="url"
                  value={opponentForm.logo}
                  onChange={(e) => setOpponentForm({...opponentForm, logo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="https://ejemplo.com/logo.png"
                />
                <button
                  type="button"
                  onClick={generateOpponentLogo}
                  className="w-full px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm"
                >
                  Generar Logo Automático
                </button>
              </div>
              {opponentForm.logo && (
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-2">Vista previa URL:</p>
                  <img
                    src={opponentForm.logo}
                    alt="Vista previa"
                    className="w-16 h-16 rounded-full object-cover border border-gray-200 mx-auto"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Información sobre prioridad */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-700">
                <i className="ri-information-line mr-1"></i>
                Si tienes ambos logos, se mostrará primero el logo subido desde medios.
              </p>
            </div>
            
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading || uploadingOpponentLogo}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Guardando...' : editingOpponent ? 'Actualizar' : 'Añadir'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowOpponentForm(false);
                  setEditingOpponent(null);
                  setOpponentForm({ name: '', logo: '', logo_img: '', city: '' });
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-md font-medium transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Editor de Imágenes para Equipos Rivales */}
      {showOpponentImageEditor && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Ajustar Logo del Equipo Rival</h3>
            
            <div className="space-y-4">
              {/* Vista previa de la imagen */}
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <img
                      src={opponentImageEditorData.originalImage}
                      alt="Preview"
                      className="max-w-full max-h-80 object-contain rounded-lg"
                      style={{ maxHeight: '320px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Controles de ajuste */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Ajuste de imagen:
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          const size = Math.min(img.width, img.height);
                          canvas.width = size;
                          canvas.height = size;
                          const x = (img.width - size) / 2;
                          const y = (img.height - size) / 2;
                          ctx?.drawImage(img, x, y, size, size, 0, 0, size, size);
                          setOpponentImageEditorData(prev => ({ ...prev, croppedImage: canvas.toDataURL() }));
                        };
                        img.src = opponentImageEditorData.originalImage;
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      Recorte Cuadrado
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setOpponentImageEditorData(prev => ({ ...prev, croppedImage: prev.originalImage }));
                      }}
                      className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                    >
                      Imagen Original
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Redimensionar:
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[200, 400, 600].map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          const canvas = document.createElement('canvas');
                          const ctx = canvas.getContext('2d');
                          const img = new Image();
                          img.onload = () => {
                            const aspectRatio = img.width / img.height;
                            let newWidth = size;
                            let newHeight = size;
                            
                            if (aspectRatio > 1) {
                              newHeight = size / aspectRatio;
                            } else {
                              newWidth = size * aspectRatio;
                            }
                            
                            canvas.width = newWidth;
                            canvas.height = newHeight;
                            ctx?.drawImage(img, 0, 0, newWidth, newHeight);
                            setOpponentImageEditorData(prev => ({ ...prev, croppedImage: canvas.toDataURL() }));
                          };
                          img.src = opponentImageEditorData.originalImage;
                        }}
                        className="px-3 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                      >
                        {size}px
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vista previa del resultado */}
              {opponentImageEditorData.croppedImage !== opponentImageEditorData.originalImage && (
                <div className="bg-gray-100 rounded-lg p-4">
                  <p className="text-sm text-gray-700 mb-2">Vista previa del resultado:</p>
                  <div className="flex justify-center">
                    <img
                      src={opponentImageEditorData.croppedImage}
                      alt="Cropped Preview"
                      className="w-20 h-20 rounded-full object-cover border border-gray-300"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                type="button"
                onClick={handleOpponentImageSave}
                disabled={uploadingOpponentLogo}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingOpponentLogo ? 'Subiendo...' : 'Usar esta imagen'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowOpponentImageEditor(false);
                  setOpponentImageEditorData({ originalImage: '', croppedImage: '', type: 'opponent_logo' });
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'sponsors' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Patrocinadores</h3>
              <p className="text-sm text-gray-500">Gestiona los patrocinadores del club</p>
            </div>
            <button
              onClick={() => {
                setEditingSponsor(null);
                setSponsorForm({ name: '', logo: '', logo_img: '', website: '' });
                setShowSponsorForm(true);
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
            >
              <i className="ri-add-line"></i>
              Añadir Patrocinador
            </button>
          </div>

          {/* Lista de patrocinadores */}
          <div className="space-y-3">
            {sponsors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <i className="ri-group-line text-4xl mb-2"></i>
                <p>No hay patrocinadores registrados</p>
                <p className="text-sm">Añade el primer patrocinador para comenzar</p>
              </div>
            ) : (
              sponsors.map((sponsor) => (
                <div key={sponsor.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {(sponsor.logo_img || sponsor.logo) && (
                      <img
                        src={sponsor.logo_img || sponsor.logo}
                        alt={sponsor.name}
                        className="w-16 h-16 object-contain border border-gray-200 rounded"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    )}
                    <div>
                      <h4 className="font-medium text-gray-900">{sponsor.name}</h4>
                      {sponsor.website && (
                        <p className="text-sm text-gray-500">
                          <i className="ri-link mr-1"></i>
                          <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                            {sponsor.website}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => editSponsor(sponsor)}
                      className="text-blue-600 hover:text-blue-700 p-2 rounded-md hover:bg-blue-50 transition-colors"
                      title="Editar"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => deleteSponsor(sponsor.id)}
                      className="text-red-600 hover:text-red-700 p-2 rounded-md hover:bg-red-50 transition-colors"
                      title="Eliminar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Formulario de Patrocinador */}
      {showSponsorForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <form onSubmit={handleSponsorSubmit} className="bg-white rounded-lg p-6 w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900">
              {editingSponsor ? 'Editar Patrocinador' : 'Añadir Patrocinador'}
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Patrocinador</label>
              <input
                type="text"
                value={sponsorForm.name}
                onChange={(e) => setSponsorForm({...sponsorForm, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Ej: Empresa S.L."
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sitio Web (opcional)</label>
              <input
                type="url"
                value={sponsorForm.website}
                onChange={(e) => setSponsorForm({...sponsorForm, website: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="https://ejemplo.com"
              />
            </div>
            
            {/* Logo desde Medios */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo desde Medios</label>
              <button
                type="button"
                onClick={openSponsorMediaSelector}
                disabled={uploadingSponsorLogo}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border-2 border-dashed border-gray-300 transition-colors disabled:opacity-50"
              >
                <i className="ri-upload-line mr-2"></i>
                {uploadingSponsorLogo ? 'Subiendo...' : sponsorForm.logo_img ? 'Cambiar Logo' : 'Subir Logo desde Dispositivo'}
              </button>
              {sponsorForm.logo_img && (
                <div className="mt-3">
                  <p className="text-sm text-gray-500 mb-2">Logo subido:</p>
                  <img
                    src={sponsorForm.logo_img}
                    alt="Logo subido"
                    className="w-32 h-16 object-contain border border-gray-200 rounded mx-auto"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setSponsorForm({ ...sponsorForm, logo_img: '' })}
                    className="mt-2 text-sm text-red-600 hover:text-red-700 block mx-auto"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Eliminar logo subido
                  </button>
                </div>
              )}
            </div>

            {/* Separador */}
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">O usar URL externa</h4>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo (URL)</label>
              <input
                type="url"
                value={sponsorForm.logo}
                onChange={(e) => setSponsorForm({...sponsorForm, logo: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="https://ejemplo.com/logo.png"
              />
              {sponsorForm.logo && (
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-2">Vista previa URL:</p>
                  <img
                    src={sponsorForm.logo}
                    alt="Vista previa"
                    className="w-32 h-16 object-contain border border-gray-200 rounded mx-auto"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Información sobre prioridad */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-700">
                <i className="ri-information-line mr-1"></i>
                Si tienes ambos logos, se mostrará primero el logo subido desde medios.
              </p>
            </div>
            
            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading || uploadingSponsorLogo}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Guardando...' : editingSponsor ? 'Actualizar' : 'Añadir'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSponsorForm(false);
                  setEditingSponsor(null);
                  setSponsorForm({ name: '', logo: '', logo_img: '', website: '' });
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-md font-medium transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Editor de Imágenes para Patrocinadores */}
      {showSponsorImageEditor && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Ajustar Logo del Patrocinador</h3>
            
            <div className="space-y-4">
              {/* Vista previa de la imagen */}
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <img
                      src={sponsorImageEditorData.croppedImage || sponsorImageEditorData.originalImage}
                      alt="Preview"
                      className="max-w-full max-h-80 object-contain rounded-lg"
                      style={{ maxHeight: '320px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Controles de ajuste */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Ajuste de imagen:
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          const size = Math.min(img.width, img.height);
                          canvas.width = size;
                          canvas.height = size;
                          const x = (img.width - size) / 2;
                          const y = (img.height - size) / 2;
                          ctx?.drawImage(img, x, y, size, size, 0, 0, size, size);
                          setSponsorImageEditorData(prev => ({ ...prev, croppedImage: canvas.toDataURL() }));
                        };
                        img.src = sponsorImageEditorData.originalImage;
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                    >
                      Recorte Cuadrado
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSponsorImageEditorData(prev => ({ ...prev, croppedImage: prev.originalImage }));
                      }}
                      className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                    >
                      Imagen Original
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Redimensionar:
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[200, 400, 600].map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          const canvas = document.createElement('canvas');
                          const ctx = canvas.getContext('2d');
                          const img = new Image();
                          img.onload = () => {
                            const aspectRatio = img.width / img.height;
                            let newWidth = size;
                            let newHeight = size;
                            
                            if (aspectRatio > 1) {
                              newHeight = size / aspectRatio;
                            } else {
                              newWidth = size * aspectRatio;
                            }
                            
                            canvas.width = newWidth;
                            canvas.height = newHeight;
                            ctx?.drawImage(img, 0, 0, newWidth, newHeight);
                            setSponsorImageEditorData(prev => ({ ...prev, croppedImage: canvas.toDataURL() }));
                          };
                          img.src = sponsorImageEditorData.originalImage;
                        }}
                        className="px-3 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                      >
                        {size}px
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Vista previa del resultado final */}
              <div className="bg-gray-100 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-2">Vista previa del resultado final:</p>
                <div className="flex justify-center">
                  <img
                    src={sponsorImageEditorData.croppedImage || sponsorImageEditorData.originalImage}
                    alt="Final Preview"
                    className="w-32 h-16 object-contain border border-gray-300 rounded bg-white p-2"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Esta será la imagen que se subirá a Supabase
                </p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                type="button"
                onClick={handleSponsorImageSave}
                disabled={uploadingSponsorLogo}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingSponsorLogo ? 'Subiendo...' : 'Usar esta imagen'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSponsorImageEditor(false);
                  setSponsorImageEditorData({ originalImage: '', croppedImage: '', type: 'sponsor_logo' });
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded font-medium"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-md text-sm ${
          message.includes('Error') 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default SiteSettingsManager;
